import React, { FC, useState, useEffect, useRef, useCallback } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import cx from 'classnames';
import { Check, Warning } from '@phosphor-icons/react';
import { Flex, Spinner, Box } from '@chakra-ui/react';

import GenericModalV2 from '../../../shared/GenericModal/GenericModal';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import { CustomBadge } from '../../../shared/CustomBadge/CustomBadge';
import Typhography from '../../../shared/Typhography/Typhography';
import CustomSelectInput from '../../../shared/CustomSelectInput/CustomSelectInput';
import { CustomFormV3 } from '../../../shared/CustomFormV3/CustomForm';
import { FieldDef } from '../../../shared/CustomFormV3/types';
import { applyServerErrors } from '../../../shared/CustomFormV3/serverErrors';

import { useEndpoints } from '../../../hooks/useEndpoints';
import { formatFullName } from '../../../utils/functions';

import {
  BLOCK_TYPE,
  BLOCK_TYPE_COLORS,
  BLOCK_TYPE_LABELS,
  EXERCISE_LOG_STATUS,
  WORKOUT_LOG_STATUS,
  WORKOUT_TYPE,
  WORKOUT_CATEGORY,
  PAIN_ZONES_LABELS,
} from '../../../enums/programming';
import {
  IWorkout,
  IWorkoutLog,
  IExerciseLog,
} from '../../../models/programming';
import { IUser2 } from '../../../models/user copy';

import styles from './WorkoutPlanCompileModal.module.scss';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WorkoutPlanCompileModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignmentId: string;
  eventId?: string;
  workout: IWorkout | null;
  preSelectedAthleteId?: string;
  athletes: IUser2[];
  isPlaylist?: boolean;
}

interface LocalSetLog {
  setNumber: number;
  repsActual: number | '';
  loadActual: number | '';
  rpe: number | '';
  completed: boolean;
  compiledByCoach: boolean;
}

interface LocalExerciseLog {
  exerciseId: string;
  exerciseName: string;
  blockType: BLOCK_TYPE;
  blockTitle: string;
  blockColor: string;
  status: EXERCISE_LOG_STATUS;
  sets: LocalSetLog[];
}

interface Step1Form {
  athleteId: string;
  dayNumber: number;
  executionDate: string;
}

interface Step3Form {
  rpe: number;
  notes: string;
  rtpPainNrs: number;
  rtpPainZone: string;
  rtpConfidence: number;
  rtpFear: number;
  rtpNotes: string;
}

// ─── Schemas ──────────────────────────────────────────────────────────────────

const step1Schema = Joi.object({
  athleteId: Joi.string().required().messages({
    'string.empty': 'Seleziona un atleta',
    'any.required': 'Seleziona un atleta',
  }),
  dayNumber: Joi.number().required(),
  executionDate: Joi.string().allow('', null).optional(),
});

const step3Schema = Joi.object({
  rpe: Joi.number().min(1).max(10).required().messages({
    'number.min': 'RPE obbligatorio',
    'any.required': 'RPE obbligatorio',
  }),
  notes: Joi.string().allow('', null).optional(),
  rtpPainNrs: Joi.number().min(0).max(10).optional(),
  rtpPainZone: Joi.string().allow('', null).optional(),
  rtpConfidence: Joi.number().min(0).max(10).optional(),
  rtpFear: Joi.number().min(0).max(10).optional(),
  rtpNotes: Joi.string().allow('', null).optional(),
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const RPE_DESCRIPTIONS: Record<number, string> = {
  1: 'Sforzo minimo',
  2: 'Molto facile',
  3: 'Facile',
  4: 'Abbastanza facile',
  5: 'Moderato',
  6: 'Abbastanza difficile',
  7: 'Difficile',
  8: 'Molto difficile',
  9: 'Quasi massimale',
  10: 'Massimale',
};

const getRpeDescription = (value: number): string => {
  const floor = Math.floor(value);
  return RPE_DESCRIPTIONS[floor] ?? RPE_DESCRIPTIONS[Math.ceil(value)] ?? '';
};

const buildExerciseLogs = (
  workout: IWorkout,
  dayNumber: number,
): LocalExerciseLog[] => {
  const day = workout.days.find((d) => d.dayNumber === dayNumber);
  if (!day) return [];

  const result: LocalExerciseLog[] = [];

  for (const block of day.blocks) {
    for (const ex of block.exercises) {
      const exName =
        ex.exercise?.name?.it ||
        ex.exercise?.name?.en ||
        ex.exerciseId;

      // Cast esplicito a `| undefined`: senza `noUncheckedIndexedAccess` l'accesso
      // a `loads?.[0]?.value` è tipato come `number` puro (mai undefined), quindi
      // `?? ''` collasserebbe il tipo a solo `number`, rompendo il confronto sotto.
      const firstLoad = (ex.loads?.[0]?.value as number | undefined) ?? '';

      const sets: LocalSetLog[] = Array.from({ length: ex.sets }, (_, i) => ({
        setNumber: i + 1,
        repsActual: '',
        loadActual: firstLoad !== '' ? (firstLoad as number) : '',
        rpe: '',
        completed: false,
        compiledByCoach: true,
      }));

      result.push({
        exerciseId: ex.exerciseId,
        exerciseName: exName,
        blockType: block.type,
        blockTitle: block.title,
        blockColor: BLOCK_TYPE_COLORS[block.type] ?? '#888',
        status: EXERCISE_LOG_STATUS.COMPLETED,
        sets,
      });
    }
  }

  return result;
};

const toApiExerciseLogs = (local: LocalExerciseLog[]): IExerciseLog[] =>
  local.map((ex) => ({
    exerciseId: ex.exerciseId,
    blockType: ex.blockType,
    status: ex.status,
    sets: ex.sets.map((s) => ({
      setNumber: s.setNumber,
      repsActual: typeof s.repsActual === 'number' ? s.repsActual : 0,
      loadActual: typeof s.loadActual === 'number' ? s.loadActual : undefined,
      rpe: typeof s.rpe === 'number' ? s.rpe : undefined,
      completed: s.completed,
      compiledByCoach: s.compiledByCoach,
    })),
  }));

const hydrateLogs = (
  local: LocalExerciseLog[],
  apiLogs: IExerciseLog[],
): LocalExerciseLog[] =>
  local.map((ex) => {
    const found = apiLogs.find((a) => a.exerciseId === ex.exerciseId);
    if (!found) return ex;
    return {
      ...ex,
      status: found.status,
      sets: ex.sets.map((s) => {
        const apiSet = found.sets.find((a) => a.setNumber === s.setNumber);
        if (!apiSet) return s;
        return {
          ...s,
          repsActual: apiSet.repsActual ?? '',
          loadActual: apiSet.loadActual ?? '',
          rpe: apiSet.rpe ?? '',
          completed: apiSet.completed ?? false,
          compiledByCoach: apiSet.compiledByCoach ?? true,
        };
      }),
    };
  });

// ─── Component ────────────────────────────────────────────────────────────────

const WorkoutPlanCompileModal: FC<WorkoutPlanCompileModalProps> = ({
  isOpen,
  onClose,
  assignmentId,
  eventId,
  workout,
  preSelectedAthleteId,
  athletes,
  isPlaylist = false,
}) => {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();

  // ── Step state
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // ── Step 1 extra state
  const [isStarting, setIsStarting] = useState(false);
  const [resumeLog, setResumeLog] = useState<IWorkoutLog | null>(null);

  // ── Step 2
  const [currentLogId, setCurrentLogId] = useState<string>('');
  const [exerciseLogs, setExerciseLogs] = useState<LocalExerciseLog[]>([]);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>(
    'idle',
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Step 3 extra state
  const [isCompleting, setIsCompleting] = useState(false);

  // ── RTP rehab detection
  const isRtpRehab =
    workout?.workoutCategory === WORKOUT_CATEGORY.RTP_REHAB ||
    (workout as any)?.category === WORKOUT_CATEGORY.RTP_REHAB;

  const days = workout?.days ?? [];
  const isMultiDay = workout?.type === WORKOUT_TYPE.MULTI_DAY;

  // ── Step 1 form
  const step1Methods = useForm<Step1Form>({
    resolver: joiResolver(step1Schema),
    defaultValues: {
      athleteId: preSelectedAthleteId || '',
      dayNumber: workout?.days?.[0]?.dayNumber ?? 1,
      executionDate: dayjs().format('YYYY-MM-DD'),
    },
  });

  // ── Step 3 form
  const step3Methods = useForm<Step3Form>({
    resolver: joiResolver(step3Schema),
    defaultValues: {
      rpe: 5,
      notes: '',
      rtpPainNrs: 0,
      rtpPainZone: '',
      rtpConfidence: 5,
      rtpFear: 0,
      rtpNotes: '',
    },
  });

  // ── Reset on open/close
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setResumeLog(null);
      setCurrentLogId('');
      setExerciseLogs([]);
      setSaveStatus('idle');

      step1Methods.reset({
        athleteId: preSelectedAthleteId || '',
        dayNumber: workout?.days?.[0]?.dayNumber ?? 1,
        executionDate: dayjs().format('YYYY-MM-DD'),
      });

      step3Methods.reset({
        rpe: 5,
        notes: '',
        rtpPainNrs: 0,
        rtpPainZone: '',
        rtpConfidence: 5,
        rtpFear: 0,
        rtpNotes: '',
      });
    }
  }, [isOpen, preSelectedAthleteId, workout]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Check for existing in_progress log when athlete + day changes
  const watchedAthleteId = step1Methods.watch('athleteId');
  const watchedDayNumber = step1Methods.watch('dayNumber');

  useEffect(() => {
    if (!watchedAthleteId || !assignmentId) return;

    endpoints
      .getAthleteLogs(watchedAthleteId, {
        workoutAssignmentId: assignmentId,
        status: WORKOUT_LOG_STATUS.IN_PROGRESS,
        limit: 5,
      })
      .then((res) => {
        const inProgress = res.data.docs.find(
          (l) => l.dayNumber === watchedDayNumber,
        );
        setResumeLog(inProgress ?? null);
      })
      .catch(() => {
        setResumeLog(null);
      });
  }, [watchedAthleteId, watchedDayNumber, assignmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Debounced save
  const scheduleSave = useCallback(
    (logs: LocalExerciseLog[]) => {
      if (!currentLogId) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setSaveStatus('saving');
      debounceRef.current = setTimeout(async () => {
        try {
          await endpoints.patchWorkoutLog(currentLogId, {
            exercises: toApiExerciseLogs(logs),
          });
          setSaveStatus('saved');
        } catch {
          setSaveStatus('idle');
        }
      }, 1000);
    },
    [currentLogId, endpoints],
  );

  const updateSet = (
    exIdx: number,
    setIdx: number,
    field: keyof LocalSetLog,
    value: any,
  ) => {
    setExerciseLogs((prev) => {
      const next = prev.map((ex, i) => {
        if (i !== exIdx) return ex;
        return {
          ...ex,
          sets: ex.sets.map((s, j) => {
            if (j !== setIdx) return s;
            return { ...s, [field]: value };
          }),
        };
      });
      scheduleSave(next);
      return next;
    });
  };

  const updateExerciseStatus = (
    exIdx: number,
    status: EXERCISE_LOG_STATUS,
  ) => {
    setExerciseLogs((prev) => {
      const next = prev.map((ex, i) =>
        i === exIdx ? { ...ex, status } : ex,
      );
      scheduleSave(next);
      return next;
    });
  };

  // ── Start session
  const handleStartSession = step1Methods.handleSubmit(async (formData) => {
    setIsStarting(true);
    try {
      let logId: string;
      let apiLogs: IExerciseLog[] = [];

      if (resumeLog) {
        logId = resumeLog._id;
        apiLogs = resumeLog.exercises;
      } else {
        const res = await endpoints.postWorkoutLog({
          workoutAssignmentId: assignmentId,
          athleteId: formData.athleteId,
          dayNumber: formData.dayNumber,
          date: isPlaylist ? formData.executionDate : dayjs().format('YYYY-MM-DD'),
          eventId: eventId,
        });
        logId = res.data._id;
        apiLogs = res.data.exercises ?? [];
      }

      setCurrentLogId(logId);

      if (workout) {
        const fresh = buildExerciseLogs(workout, formData.dayNumber);
        const hydrated = apiLogs.length > 0 ? hydrateLogs(fresh, apiLogs) : fresh;
        setExerciseLogs(hydrated);
      }

      setStep(2);
    } catch (err: any) {
      // 400 di validazione: errori sotto ai campi via RHF
      if (applyServerErrors(err, step1Methods.setError)) {
        toast.error('Controlla i campi evidenziati');
        return;
      }
      toast.error(
        err?.response?.data?.message ?? 'Errore avvio sessione',
      );
    } finally {
      setIsStarting(false);
    }
  });

  // ── Complete session
  const handleComplete = step3Methods.handleSubmit(async (formData) => {
    if (!currentLogId) return;
    setIsCompleting(true);
    try {
      await endpoints.patchWorkoutLog(currentLogId, {
        exercises: toApiExerciseLogs(exerciseLogs),
      });

      const completeBody: any = { rpe: formData.rpe };
      if (formData.notes?.trim()) completeBody.notes = formData.notes.trim();
      if (isRtpRehab) {
        completeBody.rtpPainNrs = formData.rtpPainNrs;
        if (formData.rtpPainZone) completeBody.rtpPainZone = formData.rtpPainZone;
        completeBody.rtpConfidence = formData.rtpConfidence;
        completeBody.rtpFear = formData.rtpFear;
        if (formData.rtpNotes?.trim()) completeBody.rtpNotes = formData.rtpNotes.trim();
      }
      await endpoints.completeWorkoutLog(currentLogId, completeBody);

      await queryClient.invalidateQueries({ queryKey: ['workoutLogs'] });
      await queryClient.invalidateQueries({ queryKey: ['athleteLogs'] });
      await queryClient.invalidateQueries({ queryKey: ['programmingEvents'] });

      toast.success('Sessione completata!');
      onClose();
    } catch (err: any) {
      // 400 di validazione: errori sotto ai campi via RHF
      if (applyServerErrors(err, step3Methods.setError)) {
        toast.error('Controlla i campi evidenziati');
        return;
      }
      toast.error(
        err?.response?.data?.message ?? 'Errore completamento sessione',
      );
    } finally {
      setIsCompleting(false);
    }
  });

  // ── Athlete options
  const athleteOptions = athletes.map((a) => ({
    label: formatFullName(a.firstName, a.lastName),
    value: a._id,
  }));

  // ─── Step 1 layout ─────────────────────────────────────────────────────────

  const step1Layout: FieldDef[] = [
    {
      key: 'athleteId',
      type: 'single-select',
      label: 'Atleta',
      placeholder: 'Seleziona atleta...',
      size: 12,
      options: athleteOptions,
    },
    ...(isMultiDay && days.length > 1
      ? [
          {
            key: 'dayNumber',
            type: 'custom' as const,
            size: 12,
            label: 'Giorno',
            render: (field: any) => (
              <div className="d-flex gap-2 flex-wrap">
                {days.map((d) => (
                  <CustomButton
                    key={d.dayNumber}
                    color={field.value === d.dayNumber ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => field.onChange(d.dayNumber)}
                  >
                    {d.title ? d.title : `Giorno ${d.dayNumber}`}
                  </CustomButton>
                ))}
              </div>
            ),
          },
        ]
      : []),
    ...(isPlaylist
      ? [
          {
            key: 'executionDate',
            type: 'date' as const,
            label: 'Data esecuzione',
            size: 12,
          },
        ]
      : []),
  ];

  // ─── Step 3 layouts ────────────────────────────────────────────────────────

  const step3BaseLayout: FieldDef[] = [
    {
      key: 'rpe',
      type: 'custom',
      label: 'RPE Sessione',
      size: 12,
      render: (field: any) => (
        <div>
          <Typhography size="regular-12" className="color-text-2" style={{ marginBottom: 8 }}>
            Quanto è stato impegnativo l'allenamento?
          </Typhography>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>1</span>
            <input
              type="range"
              min={1}
              max={10}
              step={0.5}
              value={field.value}
              onChange={(e) => field.onChange(parseFloat(e.target.value))}
              style={{ flex: 1, accentColor: '#063444' }}
            />
            <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>10</span>
          </div>
          <div style={{ textAlign: 'center', marginTop: 4 }}>
            <span style={{ fontSize: 28, fontWeight: 700, color: '#063444' }}>{field.value}</span>
          </div>
          <div style={{ textAlign: 'center', marginTop: 2 }}>
            <Typhography size="regular-12" className="color-text-2">
              {getRpeDescription(field.value)}
            </Typhography>
          </div>
        </div>
      ),
    },
    {
      key: 'notes',
      type: 'text-area',
      label: 'Note sessione',
      placeholder: 'Note opzionali sulla sessione...',
      size: 12,
    },
  ];

  const step3RtpLayout: FieldDef[] = [
    {
      key: 'rtpPainNrs',
      type: 'custom',
      label: 'Dolore (NRS)',
      size: 12,
      render: (field: any) => (
        <div>
          <Typhography size="regular-12" className="color-text-2" style={{ marginBottom: 8 }}>
            0 = nessun dolore, 10 = dolore massimo
          </Typhography>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>0</span>
            <input
              type="range"
              min={0}
              max={10}
              step={1}
              value={field.value}
              onChange={(e) => field.onChange(parseInt(e.target.value))}
              style={{ flex: 1, accentColor: '#f05555' }}
            />
            <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>10</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: field.value > 5 ? '#f05555' : '#00212c', minWidth: 30, textAlign: 'center' as const }}>
              {field.value}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'rtpPainZone',
      type: 'single-select',
      label: 'Zona dolore',
      placeholder: 'Seleziona zona...',
      size: 12,
      options: Object.entries(PAIN_ZONES_LABELS).map(([key, label]) => ({ label, value: key })),
    },
    {
      key: 'rtpConfidence',
      type: 'custom',
      label: 'Fiducia nel movimento',
      size: 12,
      render: (field: any) => (
        <div>
          <Typhography size="regular-12" className="color-text-2" style={{ marginBottom: 8 }}>
            0 = nessuna fiducia, 10 = fiducia completa
          </Typhography>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>0</span>
            <input
              type="range"
              min={0}
              max={10}
              step={1}
              value={field.value}
              onChange={(e) => field.onChange(parseInt(e.target.value))}
              style={{ flex: 1, accentColor: '#4dd292' }}
            />
            <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>10</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--c-text-0)', minWidth: 30, textAlign: 'center' as const }}>
              {field.value}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'rtpFear',
      type: 'custom',
      label: 'Paura / Apprensione',
      size: 12,
      render: (field: any) => (
        <div>
          <Typhography size="regular-12" className="color-text-2" style={{ marginBottom: 8 }}>
            0 = nessuna paura, 10 = paura massima
          </Typhography>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>0</span>
            <input
              type="range"
              min={0}
              max={10}
              step={1}
              value={field.value}
              onChange={(e) => field.onChange(parseInt(e.target.value))}
              style={{ flex: 1, accentColor: '#f5b350' }}
            />
            <span style={{ fontSize: 12, color: 'var(--c-text-3)' }}>10</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--c-text-0)', minWidth: 30, textAlign: 'center' as const }}>
              {field.value}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'rtpNotes',
      type: 'text-area',
      label: 'Note fisioterapista',
      placeholder: 'Osservazioni cliniche...',
      size: 12,
    },
  ];

  // ─── Render helpers ────────────────────────────────────────────────────────

  const renderStepIndicator = () => (
    <div className={styles.stepIndicator}>
      {([1, 2, 3] as const).map((s, idx) => (
        <React.Fragment key={s}>
          <div
            className={cx(styles.stepDot, {
              [styles.active]: step === s,
              [styles.done]: step > s,
            })}
          >
            {step > s ? <Check size={14} weight="bold" /> : s}
          </div>
          {idx < 2 && (
            <div
              className={cx(styles.stepLine, {
                [styles.active]: step > s,
              })}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className={styles.content}>
      {workout && (
        <div className={styles.workoutInfo}>
          <p className={styles.workoutTitle}>{workout.title}</p>
          <div className={styles.workoutMeta}>
            <span className={styles.typeBadge}>
              {isMultiDay ? 'Multi-giorno' : 'Giorno singolo'}
            </span>
            {workout.estimatedDuration && (
              <Typhography size="regular-12" className={styles.typeBadge}>
                ~{workout.estimatedDuration} min
              </Typhography>
            )}
          </div>
        </div>
      )}

      <FormProvider {...step1Methods}>
        <CustomFormV3 layout={step1Layout} />
      </FormProvider>

      {resumeLog && (
        <div className={styles.resumeBanner}>
          <Warning size={18} weight="fill" />
          <span>
            Sessione in corso trovata — verrà ripresa dal punto in cui era
            rimasta.
          </span>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className={styles.content}>
      {exerciseLogs.map((ex, exIdx) => (
        <div
          key={`${ex.exerciseId}-${exIdx}`}
          className={styles.exerciseBlock}
          style={{ '--block-color': ex.blockColor } as React.CSSProperties}
        >
          <div className={styles.blockHeader}>
            <CustomBadge
              label={BLOCK_TYPE_LABELS[ex.blockType] ?? ex.blockType}
              color="lightblue"
            />
            <p className={styles.exerciseName}>{ex.exerciseName}</p>
          </div>

          <div className={styles.statusRow}>
            <span className={styles.statusLabel}>Stato:</span>
            <div className={styles.statusSelect}>
              <CustomSelectInput
                options={[
                  { label: 'Completato', value: EXERCISE_LOG_STATUS.COMPLETED },
                  { label: 'Parziale', value: EXERCISE_LOG_STATUS.PARTIAL },
                  { label: 'Saltato', value: EXERCISE_LOG_STATUS.SKIPPED },
                ]}
                value={ex.status}
                onChange={(v) =>
                  updateExerciseStatus(exIdx, v as EXERCISE_LOG_STATUS)
                }
                placeholder="Stato..."
              />
            </div>
          </div>

          {ex.status !== EXERCISE_LOG_STATUS.SKIPPED && (
            <>
              <div className={styles.setHeader}>
                <span className={styles.setHeaderCell}>Set</span>
                <span className={styles.setHeaderCell}>Rip.</span>
                <span className={styles.setHeaderCell}>Carico</span>
                <span className={styles.setHeaderCell}>RPE</span>
                <span className={styles.setHeaderCell}>OK</span>
              </div>
              {ex.sets.map((s, setIdx) => (
                <div key={s.setNumber} className={styles.setRow}>
                  <span className={styles.setNumberCell}>{s.setNumber}</span>
                  <input
                    type="number"
                    className={styles.setInput}
                    value={s.repsActual}
                    placeholder="—"
                    min={0}
                    onChange={(e) =>
                      updateSet(
                        exIdx,
                        setIdx,
                        'repsActual',
                        e.target.value === '' ? '' : Number(e.target.value),
                      )
                    }
                  />
                  <input
                    type="number"
                    className={styles.setInput}
                    value={s.loadActual}
                    placeholder="—"
                    min={0}
                    step={0.5}
                    onChange={(e) =>
                      updateSet(
                        exIdx,
                        setIdx,
                        'loadActual',
                        e.target.value === '' ? '' : Number(e.target.value),
                      )
                    }
                  />
                  <input
                    type="number"
                    className={styles.setInput}
                    value={s.rpe}
                    placeholder="—"
                    min={1}
                    max={10}
                    step={0.5}
                    onChange={(e) =>
                      updateSet(
                        exIdx,
                        setIdx,
                        'rpe',
                        e.target.value === '' ? '' : Number(e.target.value),
                      )
                    }
                  />
                  <input
                    type="checkbox"
                    className={styles.setCheckbox}
                    checked={s.completed}
                    onChange={(e) =>
                      updateSet(exIdx, setIdx, 'completed', e.target.checked)
                    }
                  />
                </div>
              ))}
            </>
          )}
        </div>
      ))}
    </div>
  );

  const renderStep3 = () => (
    <div className={styles.content}>
      <Typhography size="bold-16" className="text-center">
        Come è stata la sessione?
      </Typhography>

      <FormProvider {...step3Methods}>
        <CustomFormV3 layout={step3BaseLayout} />

        {isRtpRehab && (
          <div style={{ marginTop: 16, padding: 16, borderRadius: 12, background: 'var(--c-app-bg)', border: '1px solid #e7e7e7' }}>
            <Typhography size="bold-14" style={{ marginBottom: 12 }}>Questionario RTP</Typhography>
            <CustomFormV3 layout={step3RtpLayout} />
          </div>
        )}
      </FormProvider>
    </div>
  );

  // ─── Footer ────────────────────────────────────────────────────────────────

  const renderFooter = () => {
    if (step === 1) {
      return (
        <div className={styles.footer}>
          <CustomButton color="outline" onClick={onClose}>
            Annulla
          </CustomButton>
          <CustomButton
            color="primary"
            onClick={handleStartSession}
            isLoading={isStarting}
            disabled={isStarting}
          >
            {resumeLog ? 'Riprendi Sessione' : 'Avvia Sessione'}
          </CustomButton>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className={styles.footer}>
          <div
            className={cx(styles.saveIndicator, {
              [styles.saving]: saveStatus === 'saving',
            })}
          >
            {saveStatus === 'saving' && (
              <>
                <Spinner size="xs" />
                <span>Salvataggio...</span>
              </>
            )}
            {saveStatus === 'saved' && (
              <>
                <Check size={14} weight="bold" />
                <span>Salvato</span>
              </>
            )}
          </div>
          <div className={styles.footerRight}>
            <CustomButton color="primary" onClick={() => setStep(3)}>
              Completa Sessione
            </CustomButton>
          </div>
        </div>
      );
    }

    // step 3
    return (
      <div className={styles.footer}>
        <CustomButton color="outline" onClick={() => setStep(2)}>
          Indietro
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={handleComplete}
          isLoading={isCompleting}
          disabled={isCompleting}
        >
          Conferma
        </CustomButton>
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  const stepTitles: Record<1 | 2 | 3, string> = {
    1: 'Avvia Sessione',
    2: 'Compila Allenamento',
    3: 'RPE Sessione',
  };

  return (
    <GenericModalV2
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={stepTitles[step]}
      Header={
        <Box pb="12px">
          <Flex align="center" justify="space-between" mb="12px">
            <Typhography size="bold-24">{stepTitles[step]}</Typhography>
          </Flex>
          {renderStepIndicator()}
        </Box>
      }
      Footer={renderFooter()}
    >
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </GenericModalV2>
  );
};

export default WorkoutPlanCompileModal;
